import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Trophy, Flame, Target, Award, ChevronUp, ChevronDown, RotateCcw, Save, Loader2
} from "lucide-react";

const categories = [
  { key: "consistency", label: "Consistency Champion", icon: Flame, headerBg: "bg-blue-50 border-blue-100", rankBg: "bg-blue-100 text-blue-700", btnBg: "bg-blue-600 hover:bg-blue-700", desc: "Most days with 3+ meals logged" },
  { key: "low_calorie", label: "Low Calorie", icon: Target, headerBg: "bg-emerald-50 border-emerald-100", rankBg: "bg-emerald-100 text-emerald-700", btnBg: "bg-emerald-600 hover:bg-emerald-700", desc: "Lowest avg calories per meal" },
  { key: "health_hero", label: "Health Hero", icon: Trophy, headerBg: "bg-amber-50 border-amber-100", rankBg: "bg-amber-100 text-amber-700", btnBg: "bg-amber-600 hover:bg-amber-700", desc: "Highest health score (AI + your ratings)" },
];

const DietitianLeaderboard = () => {
  const { token } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState("");
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editedRankings, setEditedRankings] = useState({});
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const res = await fetch(`${API}/corporate/dietitian/programs`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const d = await res.json();
          setPrograms(d.programs || []);
          if (d.programs?.length > 0) setSelectedProgram(d.programs[0].program_id);
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchPrograms();
  }, [token]);

  const fetchLeaderboard = async () => {
    if (!selectedProgram) return;
    try {
      const res = await fetch(`${API}/corporate/dietitian/programs/${selectedProgram}/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLeaderboard(data);
        setEditedRankings({});
      }
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    if (!selectedProgram) return;
    setLoading(true);
    fetchLeaderboard().finally(() => setLoading(false));
  }, [selectedProgram, token]);

  const getList = (category) => {
    return editedRankings[category] || leaderboard?.[category] || [];
  };

  const moveEntry = (category, index, direction) => {
    const list = [...getList(category)];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= list.length) return;
    [list[index], list[newIndex]] = [list[newIndex], list[index]];
    list.forEach((item, i) => { item.rank = i + 1; });
    setEditedRankings(prev => ({ ...prev, [category]: list }));
  };

  const saveCategory = async (category) => {
    const list = editedRankings[category];
    if (!list) return;
    setSaving(category);
    try {
      const res = await fetch(`${API}/corporate/dietitian/programs/${selectedProgram}/leaderboard`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ category, order: list.map(e => e.user_id) })
      });
      if (res.ok) {
        toast.success(`${categories.find(c => c.key === category)?.label} rankings saved!`);
        await fetchLeaderboard();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save");
      }
    } catch { toast.error("Error saving rankings"); }
    finally { setSaving(null); }
  };

  const resetCategory = async (category) => {
    setSaving(category);
    try {
      const res = await fetch(`${API}/corporate/dietitian/programs/${selectedProgram}/leaderboard/${category}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("Reset to auto-computed rankings");
        await fetchLeaderboard();
      }
    } catch { toast.error("Error resetting"); }
    finally { setSaving(null); }
  };

  const statLabel = (category, entry) => {
    if (category === "consistency") return `${entry.perfect_days} perfect days, ${entry.streak_count} streak`;
    if (category === "low_calorie") return `${entry.avg_calories} avg kcal, ${entry.total_meals} meals`;
    return `${entry.avg_health_score}/5 health, ${entry.photo_reviews} reviews`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-secondary" data-testid="leaderboard-title">
            <Award className="w-5 h-5 inline mr-2 text-amber-500" />
            Leaderboard Management
          </h2>
          <p className="text-sm text-muted-foreground">Review auto-rankings and adjust where needed. Your input overrides AI calculations.</p>
        </div>
        {programs.length > 0 && (
          <Select value={selectedProgram} onValueChange={setSelectedProgram}>
            <SelectTrigger className="w-[220px] rounded-xl" data-testid="lb-program-select">
              <SelectValue placeholder="Select program" />
            </SelectTrigger>
            <SelectContent>
              {programs.map(p => (
                <SelectItem key={p.program_id} value={p.program_id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {leaderboard?.has_overrides && (
        <Badge className="bg-amber-100 text-amber-700 text-xs py-1 px-3 rounded-xl">
          You have manual overrides active on this leaderboard
        </Badge>
      )}

      {loading ? (
        <div className="space-y-4">{[1, 2, 3].map(i => <div key={i} className="animate-pulse h-40 bg-white rounded-2xl" />)}</div>
      ) : !leaderboard ? (
        <Card className="rounded-2xl p-8 text-center">
          <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-muted-foreground">No leaderboard data available yet</p>
        </Card>
      ) : (
        <div className="space-y-6">
          {categories.map(({ key, label, icon: Icon, headerBg, rankBg, btnBg, desc }) => {
            const list = getList(key);
            const isEdited = !!editedRankings[key];

            return (
              <Card key={key} className="rounded-2xl border overflow-hidden" data-testid={`lb-category-${key}`}>
                <div className={`${headerBg} px-5 py-3 border-b flex items-center justify-between`}>
                  <div className="flex items-center gap-2">
                    <Icon className="w-5 h-5" />
                    <div>
                      <h3 className="font-semibold text-secondary text-sm">{label}</h3>
                      <p className="text-[10px] text-muted-foreground">{desc}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {isEdited && (
                      <Button size="sm" onClick={() => saveCategory(key)} disabled={saving === key}
                        className={`${btnBg} rounded-xl text-xs h-8 text-white`}
                        data-testid={`save-${key}`}>
                        {saving === key ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                        Save
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => resetCategory(key)} disabled={saving === key}
                      className="rounded-xl text-xs h-8 text-muted-foreground hover:text-red-500"
                      data-testid={`reset-${key}`}>
                      <RotateCcw className="w-3 h-3 mr-1" /> Reset
                    </Button>
                  </div>
                </div>
                <CardContent className="p-0">
                  {list.length === 0 ? (
                    <p className="text-sm text-muted-foreground p-5 text-center">No participants yet</p>
                  ) : (
                    <div className="divide-y">
                      {list.map((entry, i) => (
                        <div key={entry.user_id} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50/50 transition-colors"
                          data-testid={`lb-entry-${key}-${i}`}>
                          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                            i === 0 ? rankBg :
                            i === 1 ? "bg-gray-100 text-gray-600" :
                            i === 2 ? "bg-orange-100 text-orange-600" : "bg-gray-50 text-gray-400"
                          }`}>{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-secondary text-sm">{entry.alias || "Participant"}</p>
                            <p className="text-[10px] text-muted-foreground">{statLabel(key, entry)}</p>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full"
                              disabled={i === 0} onClick={() => moveEntry(key, i, -1)}
                              data-testid={`move-up-${key}-${i}`}>
                              <ChevronUp className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full"
                              disabled={i === list.length - 1} onClick={() => moveEntry(key, i, 1)}
                              data-testid={`move-down-${key}-${i}`}>
                              <ChevronDown className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DietitianLeaderboard;
